// src/components/user/Masivo/DateSelector.jsx
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Clock, AlertCircle, ChevronLeft, ChevronRight, X } from 'lucide-react';

function DateSelector({
  selectedDate,
  onDateChange,
  title = 'Paso 1: Fecha de Entrega',
  description = 'Selecciona la fecha para todas las entregas de este lote',
  selectedLabel = 'Fecha seleccionada:',
  batchHelp = 'Todas las entregas del archivo usarán la misma fecha',
}) {
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');
  const [currentHour, setCurrentHour] = useState(null);
  const [canSelectToday, setCanSelectToday] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const dayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  useEffect(() => {
    // Get current Peru time from backend or use browser time
    const now = new Date();
    const hour = now.getHours();
    
    setCurrentHour(hour);
    setCanSelectToday(hour < 15); // Before 3pm
    
    // Calculate min date
    const min = new Date(now);
    if (hour >= 15) {
      // After 3pm, minimum is tomorrow
      min.setDate(min.getDate() + 1);
    }
    min.setHours(0, 0, 0, 0);
    
    // Calculate max date (30 days ahead)
    const max = new Date(now);
    max.setDate(max.getDate() + 30);
    max.setHours(23, 59, 59, 999);
    
    setMinDate(formatDateForInput(min));
    setMaxDate(formatDateForInput(max));
  }, []);

  useEffect(() => {
    if (!showCalendar || typeof document === 'undefined') return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setShowCalendar(false);
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showCalendar]);

  function formatDateForInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function formatDateForDisplay(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('es-PE', options);
  }

  function formatDateDDMMYYYY(isoDate) {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}/${month}/${year}`;
  }

  function getDaysInMonth(month, year) {
    return new Date(year, month + 1, 0).getDate();
  }

  function getFirstDayOfMonth(month, year) {
    return new Date(year, month, 1).getDay();
  }

  function generateCalendarDays() {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  }

  function handleDateSelect(day) {
    if (!day) return;

    const year = currentYear;
    const month = String(currentMonth + 1).padStart(2, '0');
    const dayPadded = String(day).padStart(2, '0');
    const dateString = `${year}-${month}-${dayPadded}`;
    
    // Check if date is within valid range
    if (dateString >= minDate && dateString <= maxDate) {
      onDateChange(dateString);
      setShowCalendar(false);
    }
  }

  function isSelectedDate(day) {
    if (!day || !selectedDate) return false;
    const [year, month, dayOfMonth] = selectedDate.split('-').map(Number);
    return (
      dayOfMonth === day &&
      (month - 1) === currentMonth &&
      year === currentYear
    );
  }

  function isDateDisabled(day) {
    if (!day) return true;
    
    const year = currentYear;
    const month = String(currentMonth + 1).padStart(2, '0');
    const dayPadded = String(day).padStart(2, '0');
    const dateString = `${year}-${month}-${dayPadded}`;
    
    return dateString < minDate || dateString > maxDate;
  }

  function goToPreviousMonth() {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  }

  function goToNextMonth() {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  }

  const calendarDays = generateCalendarDays();

  return (
    <div className="bg-white rounded-2xl border-2 border-slate-200 p-4 sm:p-6 lg:p-8">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-primary to-[#E6B43C] flex items-center justify-center flex-shrink-0">
          <Calendar className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl sm:text-2xl font-extrabold text-brand-primary font-montserrat mb-2">
            {title}
          </h2>
          <p className="text-sm sm:text-base text-slate-600 font-montserrat">
            {description}
          </p>
        </div>
      </div>

      {/* Time-based alert */}
      {currentHour !== null && (
        <div className={`rounded-xl p-4 mb-6 border-l-4 ${
          canSelectToday 
            ? 'bg-brand-green/10 border-brand-green' 
            : 'bg-[#E6B43C]/10 border-[#E6B43C]'
        }`}>
          <div className="flex items-start gap-3">
            <Clock className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              canSelectToday ? 'text-brand-green' : 'text-[#E6B43C]'
            }`} />
            <div>
              <p className="text-sm font-semibold text-slate-900 font-montserrat">
                {canSelectToday 
                  ? `✅ Entregas para HOY disponibles hasta las 1:00 PM (son las ${currentHour}:00)`
                  : `⏰ Ya pasaron las 1:00 PM - Entregas desde mañana`
                }
              </p>
              {canSelectToday && (
                <p className="text-xs text-slate-600 font-montserrat mt-1">
                  Tienes {15 - currentHour} hora(s) para agendar entregas del día de hoy
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom calendar picker */}
      <div className="space-y-3">
        <label className="block text-sm font-bold text-slate-700 font-montserrat">
          Selecciona la fecha
        </label>
        
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowCalendar(!showCalendar)}
            aria-haspopup="dialog"
            aria-expanded={showCalendar}
            className="w-full rounded-xl border-2 border-slate-300 bg-white px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all flex items-center justify-between font-montserrat hover:border-brand-primary"
          >
            <span className="text-slate-800 font-medium">
              {selectedDate ? formatDateDDMMYYYY(selectedDate) : 'Selecciona una fecha'}
            </span>
            <Calendar className="w-5 h-5 text-slate-400" />
          </button>

          {showCalendar && typeof document !== 'undefined' && createPortal(
            <div
              className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/35 p-3 sm:p-4 backdrop-blur-[1px]"
              onMouseDown={() => setShowCalendar(false)}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Seleccionar fecha de reserva"
                onMouseDown={(event) => event.stopPropagation()}
                className="w-full max-w-[21rem] max-h-[calc(100dvh-1.5rem)] overflow-y-auto overscroll-contain rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl sm:p-4"
              >
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500 font-montserrat">
                    Fecha de reserva
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowCalendar(false)}
                    aria-label="Cerrar calendario"
                    className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Month/Year Header */}
                <div className="mb-2 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={goToPreviousMonth}
                    aria-label="Mes anterior"
                    className="rounded-lg p-1.5 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  >
                    <ChevronLeft className="h-4 w-4 text-slate-600" />
                  </button>

                  <p className="text-sm font-bold text-slate-800 font-montserrat">
                    {monthNames[currentMonth]} {currentYear}
                  </p>

                  <button
                    type="button"
                    onClick={goToNextMonth}
                    aria-label="Mes siguiente"
                    className="rounded-lg p-1.5 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  >
                    <ChevronRight className="h-4 w-4 text-slate-600" />
                  </button>
                </div>

                {/* Day Names */}
                <div className="mb-1 grid grid-cols-7 gap-0.5">
                  {dayNames.map((day, index) => (
                    <div key={`${day}-${index}`} className="py-1 text-center text-[11px] font-semibold text-slate-500 font-montserrat">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-0.5">
                  {calendarDays.map((day, index) => {
                    const isSelected = isSelectedDate(day);
                    const isDisabled = isDateDisabled(day);

                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleDateSelect(day)}
                        disabled={isDisabled || !day}
                        className={`
                          h-9 rounded-md text-xs transition-all font-montserrat sm:h-10 sm:text-sm
                          ${!day ? 'invisible' : ''}
                          ${isDisabled ? 'cursor-not-allowed text-slate-300' : ''}
                          ${isSelected ? 'bg-brand-primary font-bold text-white shadow-md' : ''}
                          ${!isSelected && !isDisabled && day ? 'text-slate-700 hover:bg-brand-primary/10 hover:text-brand-primary' : ''}
                        `}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
        
        {selectedDate && (
          <div className="bg-gradient-to-r from-brand-primary/10 to-brand-green/10 rounded-xl p-4 border border-brand-primary/20">
            <p className="text-sm font-semibold text-slate-700 font-montserrat mb-1">
              {selectedLabel}
            </p>
            <p className="text-lg font-extrabold text-brand-primary font-montserrat capitalize">
              {formatDateForDisplay(selectedDate)}
            </p>
          </div>
        )}
      </div>

      {/* Info panel */}
      <div className="mt-6 bg-slate-50 rounded-xl p-4 border border-slate-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-slate-600 font-montserrat space-y-1">
            <p>• Puedes agendar entregas hasta <strong>30 días adelante</strong></p>
            <p>• {batchHelp}</p>
            <p>• Puedes cambiar la fecha en cualquier momento</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DateSelector;
